import {
  Document,
  Insured,
  PersonnelMovement,
  Population,
} from '../models/models';
import { ApiService } from './api.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PopulationService {
  public insureds: BehaviorSubject<Insured[]> = new BehaviorSubject<Insured[]>(
    null
  );

  constructor(private apiService: ApiService) {}

  async getPopulations(enterpriseId: string): Promise<Population[]> {
    return new Promise(async (resolve, reject) => {
      this.apiService.get<Population[]>('/populations', true).subscribe({
        next: (res) => {
          resolve(res.map((e) => new Population(e)));
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  async getMember(memberId: string): Promise<Insured> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .get<Insured>('/populations/members/' + memberId, true)
        .subscribe({
          next: (res) => {
            resolve(new Insured(res));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async getMemberDocuments(memberId: string): Promise<Document[]> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .get<Document[]>('/members/' + memberId + '/documents', true)
        .subscribe({
          next: (res) => {
            resolve(res.map((d) => new Document(d)));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  getAllMembers(): Promise<Insured[]> {
    return new Promise(async (resolve, reject) => {
      this.apiService.get<Insured[]>('/populations/members', true).subscribe({
        next: (res) => {
          resolve(res.map((e) => new Insured(e)));
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  async createInsured(data: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService.post<any>('/populations/members', data, true).subscribe({
        next: (res) => {
          resolve(true);
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  async sendInvitations(data: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService.post<any>('/invitations', data, true).subscribe({
        next: (res) => {
          resolve(true);
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  exportMembers(
    format: 'xlsx' | 'pdf',
    fields: string[]
  ): Promise<{ url: string }> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .post<{ url: string }>(
          '/populations/members/export',
          { format, fields },
          true
        )
        .subscribe({
          next: (res) => {
            resolve(res);
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  updateMember(
    memberId: string,
    data: {
      type: 'email' | 'mobile' | 'phone';
      value: string;
    }
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .put<any>('/populations/members/' + memberId, data, true)
        .subscribe({
          next: (res) => {
            resolve(true);
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async getRadiationReasons(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      this.apiService.get<any[]>('/radiation-request-reasons', true).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  radiateMember(
    enterpriseId: string,
    memberId: string,
    effectiveDate: string,
    beneficiaryIds: string[] = [],
    reasonId?: string,
    documents?: any[]
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .post<any>(
          '/enterprises/' +
            enterpriseId +
            '/members/' +
            memberId +
            '/radiation-request',
          { effectiveDate, beneficiaryIds, reasonId, documents },
          true
        )
        .subscribe({
          next: (res) => {
            resolve(res);
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  getPersonnelMovements() {
    return this.apiService
      .get<PersonnelMovement[]>('/populations/movements', true)
      .pipe(map((e) => e.map((m) => new PersonnelMovement(m))));
  }

  async createMovements(
    data: Partial<PersonnelMovement>
  ): Promise<PersonnelMovement> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .post<PersonnelMovement>('/populations/movements', data, true)
        .subscribe({
          next: (res) => {
            resolve(new PersonnelMovement(res));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async getMemberDetails(id: string): Promise<Insured> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .get<Insured>('/populations/members/' + id + '/details', true)
        .subscribe({
          next: (res) => {
            resolve(new Insured(res));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async getBeneficiaries(memberId: string): Promise<Insured[]> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .get<Insured[]>(
          '/populations/members/' + memberId + '/beneficiaries',
          true
        )
        .subscribe({
          next: (res) => {
            resolve(res.map((b) => new Insured(b)));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async createBeneficiary(memberId: string, data: any): Promise<Insured> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .post<Insured>(
          '/populations/members/' + memberId + '/beneficiaries',
          data,
          true
        )
        .subscribe({
          next: (res) => {
            resolve(new Insured(res));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async updateBeneficiary(
    memberId: string,
    beneficiaryId: string,
    data: any
  ): Promise<Insured> {
    return new Promise(async (resolve, reject) => {
      this.apiService
        .put<Insured>(
          '/populations/members/' +
            memberId +
            '/beneficiaries/' +
            beneficiaryId,
          data,
          true
        )
        .subscribe({
          next: (res) => {
            resolve(new Insured(res));
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }
}
